import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Shield, Users, Settings, Clock, Target, BarChart3, CheckCircle, Calendar, FileText, Play, Quote, Zap, Users2, Target as TargetIcon } from 'lucide-react';

const benefits = [
  {
    icon: Zap,
    title: 'Executive-level discipline without long-term overhead',
    description: 'Get C-suite operational leadership when you need it, without the cost of a full-time executive.',
  },
  {
    icon: TargetIcon,
    title: 'Immediate pattern recognition from prior turnarounds',
    description: 'Benefit from experience across 50+ years of ownership and operational transformations.',
  },
  {
    icon: Users2,
    title: 'Objective governance alignment',
    description: 'Bring boards, ownership, and management to shared clarity on strategy and execution.',
  },
  {
    icon: Zap,
    title: 'Faster sequencing of high-impact initiatives',
    description: 'Execute multiple priorities in parallel with experienced project management.',
  },
  {
    icon: CheckCircle,
    title: 'Clear accountability structures',
    description: 'Establish reporting and metrics that drive results rather than activity.',
  },
  {
    icon: Shield,
    title: 'Ideal for boards, transitions, and portfolio assets',
    description: 'Particularly effective for nonprofit boards and private equity portfolio optimization.',
  },
];

const challenges = [
  {
    icon: TrendingUp,
    title: 'Underdeveloped Pre-Need Sales',
    description: 'Leaving predictable revenue on the table'
  },
  {
    icon: BarChart3,
    title: 'Revenue Stagnation',
    description: 'Declining burial volumes eroding asset value'
  },
  {
    icon: Settings,
    title: 'Aging Infrastructure',
    description: 'Deferred maintenance reducing property worth'
  },
  {
    icon: Shield,
    title: 'Regulatory Complexity',
    description: 'Compliance risks and audit exposure'
  },
  {
    icon: Users,
    title: 'Board Governance Issues',
    description: 'Misaligned leadership slowing decisions'
  },
  {
    icon: Clock,
    title: 'Outdated Operations',
    description: 'Manual processes limiting efficiency'
  },
];

const engagements = [
  {
    title: 'Fractional COO',
    duration: '6–18 Months',
    bestFor: ['Organizations needing hands-on leadership', 'Turnaround situations', 'Multi-location operations'],
    objectives: ['Revenue system rebuilding', 'Operational modernization', 'Governance alignment'],
  },
  {
    title: 'Revenue & Operations Diagnostic',
    duration: '90 Days',
    bestFor: ['Organizations wanting assessment first', 'Boards preparing strategic plans', 'Pre-turnaround evaluation'],
    objectives: ['Comprehensive assessment', 'Strategic roadmap', 'Priority identification'],
  },
  {
    title: 'Exit & Transition Advisory',
    duration: 'Variable',
    bestFor: ['Organizations preparing for sale', 'Family transitions', 'Portfolio exits'],
    objectives: ['EBITDA enhancement', 'Compliance strengthening', 'Operational cleanup'],
  },
  {
    title: 'Strategic Advisory Retainer',
    duration: 'Ongoing',
    bestFor: ['Boards needing ongoing guidance', 'Ownership groups', 'Complex decision support'],
    objectives: ['Executive guidance', 'Board alignment', 'Strategic planning'],
  },
];

const testimonials = [
  {
    quote: "Todd brought order and clarity to our operations within 90 days. Our board finally had the operational insight we needed to make a strategic decision.",
    attribution: "Nonprofit Cemetery Board Chair",
  },
  {
    quote: "His engagement turned a multi-year revenue decline into sustainable growth. The transformation was methodical and measured.",
    attribution: "Family-Owned Cemetery Operator",
  },
  {
    quote: "We were preparing for a complex transition and needed someone who understood both operations and governance. Todd delivered on both fronts.",
    attribution: "Private Equity Portfolio Director",
  },
];

const howWeStart = [
  {
    step: '1',
    title: 'Confidential Discussion',
    description: 'Initial conversation to understand your situation, challenges, and objectives.',
  },
  {
    step: '2',
    title: 'Assessment & Proposal',
    description: 'Outline scope, timeline, and deliverables tailored to your needs.',
  },
  {
    step: '3',
    title: 'Engagement Confirmed',
    description: 'Clear reporting structure and immediate execution begins.',
  },
];

const faqs = [
  {
    question: 'When should a cemetery hire a fractional COO instead of a full-time executive?',
    answer: 'A fractional COO is ideal when you need executive-level operational leadership but don\'t have the budget or workload for a full-time C-suite role. This includes turnaround situations, board governance transitions, multi-location operations, or specific revenue system implementations. It provides the expertise and accountability of a senior executive without the long-term overhead.',
  },
  {
    question: 'How can a cemetery increase preneed sales without adding staff?',
    answer: 'Increasing preneed sales without adding staff focuses on optimizing your existing resources: implementing referral programs with existing families, creating digital pre-need education content, training current staff on consultative selling, and leveraging automation for follow-up sequences. The key is working smarter with your current team while building systems that scale.',
  },
  {
    question: 'How do cemetery owners fix low close rates on preneed and at-need leads?',
    answer: 'Low close rates typically stem from delayed follow-up, poor lead qualification, or weak consultative skills. Fixing this requires implementing rapid response protocols (call within 5 minutes), developing structured needs assessment processes, creating compelling comparison presentations, and establishing clear accountability with tracking and coaching cycles.',
  },
  {
    question: 'How can AI answer cemetery calls after hours and capture every lead?',
    answer: 'AI-powered voice assistants can handle after-hours calls, qualify leads, answer common questions about services and pricing, and schedule appointments directly into your calendar. This ensures every inquiry gets immediate attention—capturing leads that would otherwise go to competitors who offer 24/7 responsiveness.',
  },
  {
    question: 'How do cemeteries automate tour scheduling and family follow-up?',
    answer: 'Cemetery tour automation involves implementing online booking systems, sending automated reminders, pre-populating family information for personalized visits, and creating follow-up sequences for different buyer stages. This reduces staff administrative time while ensuring no family falls through the cracks.',
  },
  {
    question: 'Should my cemetery offer preneed burial insurance, and how do I evaluate it?',
    answer: 'Offering preneed burial insurance can be a valuable revenue stream and family service, but it requires careful evaluation. Consider the carrier\'s financial strength, commission structures, compliance requirements, and how it integrates with your existing preneed burial contract options. The right program should align with your operational capabilities and family needs.',
  },
  {
    question: 'How can a cemetery reduce maintenance costs without lowering standards?',
    answer: 'Cost reduction without compromising standards involves strategic approaches: implementing predictive maintenance to prevent expensive emergency repairs, optimizing grounds care schedules, leveraging technology for efficient resource allocation, and reviewing vendor contracts for better terms. The focus is on working smarter, not cutting corners.',
  },
  {
    question: 'What KPIs should a cemetery owner review every month?',
    answer: 'Key monthly KPIs include: preneed sales volume and conversion rates, at-need market share, revenue per burial, cemetery grounds condition scores, family satisfaction ratings, staff productivity metrics, pre-need trust fund performance, and operational cost per grave site. These metrics provide the operational pulse needed for informed decision-making.',
  },
];

export default function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white pt-16 pb-12 md:pt-20 md:pb-16 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent-600/20 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-slate-700/30 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3" />
        </div>
        
        <div className="container-custom relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left - Copy */}
            <div className="lg:col-span-7">
              <h1 className="font-serif text-4xl md:text-5xl font-semibold mb-6 leading-[1.15]">Fractional COO for Cemetery Turnarounds</h1>
              <p
                className="text-lg text-gray-300 mb-6 leading-relaxed max-w-xl"
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  fontStyle: "italic",
                  textAlign: "center"
                }}>I help cemetery owners, boards, and operators reverse revenue decline, restore pricing discipline, and modernize operations without the cost of a full-time COO.</p>
              <p className="text-white font-medium mb-6 text-base bg-accent-600/20 border-l-4 border-accent-500 pl-4 py-1">Proof: 3× at-need growth, 100+ agents trained, 10× fee-revenue lift in 12 months, and 10% year-over-year property sales growth across multiple operations.</p>
              <p className="text-accent-400 font-medium mb-8">Decades of cemetery ownership, operations, and turnaround leadership</p>
              
              {/* Benefit bullets */}
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-400 flex-shrink-0" />
                  <span className="text-white font-medium">Reverse revenue decline and restore margin discipline</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-400 flex-shrink-0" />
                  <span className="text-white font-medium">Fix pricing, inventory, and operating systems</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-400 flex-shrink-0" />
                  <span className="text-white font-medium">Align governance and execution for clarity</span>
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-base px-8 py-3.5">
                  Schedule a Private Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
              
              <p className="text-gray-500 text-sm mt-4">Limited, selective engagements only. 100% confidential. A Division of Black Swan Consulting LLC</p>
            </div>

            {/* Right - Who I Work With Panel */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-sm p-6 md:p-8 text-slate-800 shadow-2xl h-full">
                
                {/* Who I Work With */}
                <h3 className="font-serif text-xl font-semibold mb-4">Who This Is For</h3>
                <p className="text-slate-600 text-sm mb-5">Cemetery and deathcare organizations:</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2 text-base text-slate-700">
                    <span className="w-1.5 h-1.5 bg-accent-600 rounded-full mt-1.5 flex-shrink-0" />Cemetery Owner</li>
                  <li className="flex items-start gap-2 text-base text-slate-700">
                    <span className="w-1.5 h-1.5 bg-accent-600 rounded-full mt-1.5 flex-shrink-0" />Non-Profit Cemetery Board</li>
                  <li className="flex items-start gap-2 text-base text-slate-700">
                    <span className="w-1.5 h-1.5 bg-accent-600 rounded-full mt-1.5 flex-shrink-0" />Cemetery Operator</li>
                  <li className="flex items-start gap-2 text-base text-slate-700">
                    <span className="w-1.5 h-1.5 bg-accent-600 rounded-full mt-1.5 flex-shrink-0" />Cemetery Management Team</li>
                  <li className="flex items-start gap-2 text-base text-slate-700">
                    <span className="w-1.5 h-1.5 bg-accent-600 rounded-full mt-1.5 flex-shrink-0" />Cemetery Investor</li>
                </ul>

                {/* Testimonial */}
                <div className="mt-4 mb-6 text-center">
                  <p className="italic text-slate-600 text-sm mb-2">"Working with Todd transformed our operations. He brought practical experience and strategic clarity that our board had been missing. Within months, we saw measurable improvement in both revenue and team alignment."</p>
                  <p className="text-slate-700 font-medium text-sm">Lucas S., Cemetery Director</p>
                  <p className="text-slate-500 text-xs">California</p>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 my-5" />

                {/* Engagement Model */}
                <h4 className="font-semibold text-sm mb-3 text-slate-800">Engagement Model</h4>
                <ul className="space-y-1 mb-6">
                  <li className="text-base text-slate-700 font-medium">Fractional COO Leadership</li>
                  <li className="text-base text-slate-700 font-medium">Project-Based Operational Turnaround</li>
                  <li className="text-base text-slate-700 font-medium">Strategic Advisory Retainer</li>
                </ul>

                {/* CTA */}
                <Link 
                  to="/contact"
                  className="btn-primary w-full justify-center text-base py-3.5"
                >
                  Schedule Private Consultation →
                </Link>
                
                <p className="text-xs text-gray-500 text-center mt-3">
                  Limited, selective engagements. Direct access to principal.
                </p>

              </div>
            </div>
          </div>

          {/* Testimonial - centered at bottom of hero */}
          <div className="mt-12 max-w-3xl mx-auto text-center">

          </div>
        </div>
      </section>
      {/* Who I Work With */}
      <section className="py-6 bg-gray-50 border-y border-gray-100">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Family-owned cemeteries
            </span>
            <span className="hidden md:inline text-gray-300">•</span>
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Nonprofit cemetery boards
            </span>
            <span className="hidden md:inline text-gray-300">•</span>
            <span className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Funeral home groups
            </span>
            <span className="hidden md:inline text-gray-300">•</span>
            <span className="flex items-center gap-2">
              <TargetIcon className="w-4 h-4" />
              Private equity firms
            </span>
          </div>
        </div>
      </section>
      {/* Why Fractional COO Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-heading">Why Fractional COO Instead of Hiring Full-Time?</h2>
            <p className="section-subheading mx-auto mt-4">
              For many cemetery and deathcare organizations, operational complexity has increased 
              — but scale does not yet justify a permanent C-suite addition.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-content mx-auto">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-gray-50 p-5 rounded-sm border border-gray-100 hover:border-accent-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                <benefit.icon className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-medium text-slate-900 mb-2 text-base">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Challenges Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-heading">Common Operational Challenges</h2>
            <p className="section-subheading mx-auto mt-4">
              When revenue systems, governance, and daily execution fall out of alignment, 
              asset value erodes and strategic options narrow.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-content mx-auto">
            {challenges.map((challenge, index) => (
              <div 
                key={index}
                className="bg-white p-5 rounded-sm border-l-4 border-accent-600 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                <challenge.icon className="w-7 h-7 text-accent-600 mb-3" />
                <h3 className="font-medium text-slate-900 mb-1 text-base">
                  {challenge.title}
                </h3>
                <p className="text-slate-600 text-base">
                  {challenge.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* About Todd Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center max-w-content mx-auto">
            {/* Left - Headshot */}
            <div className="lg:col-span-3">
              <div className="aspect-[3/4] rounded-sm overflow-hidden">
                <img 
                  src="https://assets.cdn.filesafe.space/z3heplqtd54Pl8C49tAR/media/69a990277bdf3815f86b846d.jpg" 
                  alt="Todd Sexton - Fractional COO" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right - Bio */}
            <div className="lg:col-span-9">
              <h2 className="section-heading mb-4">Todd Sexton</h2>
              <p className="text-accent-600 font-medium mb-4">Fractional COO — Cemetery & Deathcare Operations</p>
              
              <div className="space-y-4 text-slate-600 leading-relaxed text-sm">
                <p>With over 50 years of owning, operating, and restructuring existing businesses — including deep experience in cemetery and deathcare operations — Todd brings a rare combination of operational discipline and industry-specific expertise.</p>
                <p>
                  He has led distressed acquisitions, nonprofit turnarounds, large-scale pre-need 
                  sales organizations, regulatory remediation efforts, and technology modernization 
                  initiatives. He works from inside the operation — not from the sidelines.
                </p>
                <p>
                  Todd limits concurrent engagements to maintain depth, discretion, and meaningful 
                  operational involvement. Clients work directly with him — not a junior team.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-2xl font-serif text-accent-600">50+</p>
                    <p className="text-sm text-slate-500">Years Experience</p>
                  </div>
                  <div>
                    <p className="text-2xl font-serif text-accent-600">100+</p>
                    <p className="text-sm text-slate-500">Pre-Need Agents Trained</p>
                  </div>
                  <div>
                    <p className="text-2xl font-serif text-accent-600">$5M+</p>
                    <p className="text-sm text-slate-500">Policy Volume Built</p>
                  </div>
                  <div>
                    <p className="text-2xl font-serif text-accent-600">133%</p>
                    <p className="text-sm text-slate-500">Revenue Growth Achieved</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-heading text-white">What Clients Say</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-content mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-slate-800 p-6 rounded-sm hover:bg-slate-750 transition-colors"
              >
                <Quote className="w-8 h-8 text-accent-400 mb-4" />
                <p className="text-gray-300 mb-5 text-base leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <p className="text-accent-400 text-sm font-medium">
                  — {testimonial.attribution}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Engagement Models */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-heading">Engagement Models</h2>
            <p className="section-subheading mx-auto mt-4">
              All engagements begin with clear scope, defined deliverables, and agreed reporting structure.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-content mx-auto">
            {engagements.map((engagement, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-sm border border-gray-100 hover:border-accent-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-serif text-xl font-semibold text-slate-900">
                    {engagement.title}
                  </h3>
                  <span className="text-accent-600 text-sm font-medium bg-accent-50 px-3 py-1 rounded-full">
                    {engagement.duration}
                  </span>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-2">Best For</p>
                  <ul className="space-y-1">
                    {engagement.bestFor.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-base text-slate-600">
                        <span className="w-1.5 h-1.5 bg-accent-600 rounded-full mt-1.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-3 border-t border-gray-100">
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-2">Primary Outcomes</p>
                  <ul className="space-y-1">
                    {engagement.objectives.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-base text-slate-600">
                        <ArrowRight className="w-3 h-3 text-accent-600 mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5">
                  <Link to="/contact" className="text-accent-600 text-sm font-medium hover:text-accent-500 inline-flex items-center gap-1 transition-colors">
                    Discuss This Engagement <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* How We Start */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="section-heading">How We Start</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-content mx-auto">
            {howWeStart.map((step, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 bg-accent-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-serif text-xl group-hover:scale-110 transition-transform duration-300">
                  {step.step}
                </div>
                <h3 className="font-medium text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-base">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">
            Ready to Stabilize and Modernize Your Operations?
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            Schedule a confidential consultation to discuss your organization's challenges 
            and explore how experienced fractional COO leadership can drive measurable results.
          </p>
          <Link to="/contact" className="btn-primary text-base px-8 py-3.5">
            Schedule a Confidential Consultation
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="section-heading text-center mb-10">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white p-5 border border-gray-200 rounded-sm hover:border-accent-200 transition-colors">
                  <h3 className="font-medium text-slate-900 mb-2 text-base">{faq.question}</h3>
                  <p className="text-slate-600 text-base leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
